import React, { useCallback } from 'react';
import IndicatorBottomButtons from '../IndicatorBottomButtons';
import {
  IndicatorDetailSectionProps,
  sectionKeyToString,
  IndicatorDetailsRoutes,
  getPreviousAndNextSectionDetails,
} from '../utils';
import {
  HeadingProps,
  allCompletedIndicatorsHeading,
  allCompletedIndicatorsSubHeading,
  nextIndicatorHeading,
  nextIndicatorSubHeading,
  nextSectionHeading,
  nextSectionIcon,
  nextSectionSubHeading,
} from './IndicatorCompletedUtils';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { faFaceSmileHearts } from '@fortawesome/pro-light-svg-icons';
import IndicatorCompletedRender from './IndicatorCompletedRender';

interface TextProps {
  icon?: (props: HeadingProps) => IconDefinition | IconProp;
  heading: (props: HeadingProps) => React.ReactNode;
  subHeading: (props: HeadingProps) => React.ReactNode;
  nextButtonNext: string;
}

type TextSectionProps = {
  [x in 'nextSection' | 'nextIndicator' | 'allCompleted']: TextProps;
};

const textSection: TextSectionProps = {
  nextSection: {
    icon: nextSectionIcon,
    heading: nextSectionHeading,
    subHeading: nextSectionSubHeading,
    nextButtonNext: 'Let’s go!',
  },
  nextIndicator: {
    heading: nextIndicatorHeading,
    subHeading: nextIndicatorSubHeading,
    nextButtonNext: 'Next Indicator',
  },
  allCompleted: {
    icon: () => faFaceSmileHearts,
    heading: allCompletedIndicatorsHeading,
    subHeading: allCompletedIndicatorsSubHeading,
    nextButtonNext: 'Save & Review',
  },
};

const IndicatorCompleted: React.FC<Omit<
  IndicatorDetailSectionProps,
  'onNext'
> & {
  reloadAnswers: () => unknown;
  handleNext: (buttonPressed: boolean, answer?: string) => unknown;
  setRoute: (route: IndicatorDetailsRoutes) => unknown;
  onNextReportBuilderSection: () => unknown;
}> = ({
  saving,
  reportId,
  requestState,
  indicators,
  selectedIndex,
  selectedSectionKey,
  reloadAnswers,
  setSelectedIndex,
  setSelectedSectionKey,
  setRoute,
  handleNext,
  onNextReportBuilderSection,
  onPrevious,
}) => {
  const {
    nextSectionKey,
    nextIndicator,
    currentSection,
  } = getPreviousAndNextSectionDetails(
    indicators,
    selectedSectionKey,
    selectedIndex,
  );

  const text: TextProps = nextIndicator
    ? textSection['nextIndicator']
    : nextSectionKey
    ? textSection['nextSection']
    : textSection['allCompleted'];

  const headingProps: HeadingProps = {
    nextSectionName: sectionKeyToString(nextSectionKey || 'succeed'),
    currentSectionKey: selectedSectionKey,
    currentSectionName: sectionKeyToString(selectedSectionKey),
    nextSectionTotalIndicators: nextSectionKey
      ? indicators[nextSectionKey].length
      : 0,
    howManyIndicatorsLeftInCurrentSection:
      indicators[selectedSectionKey].length - selectedIndex - 1,
  };

  const handleComplete = useCallback(() => {
    if (nextIndicator) {
      setSelectedIndex(s => s + 1);
      setRoute('forecast');
      return;
    }
    if (nextSectionKey) {
      setSelectedIndex(0);
      setSelectedSectionKey(nextSectionKey);
      setRoute('forecast');
      return;
    }
    handleNext(true, JSON.stringify(indicators));
  }, [
    nextSectionKey,
    currentSection,
    nextIndicator,
    selectedIndex,
    indicators,
    setRoute,
    handleNext,
    setSelectedIndex,
    setSelectedSectionKey,
  ]);

  return (
    <IndicatorCompletedRender
      icon={text.icon?.(headingProps)}
      heading={text.heading(headingProps)}
      subheading={text.subHeading(headingProps)}
      children={
        <IndicatorBottomButtons
          requestState={saving ? 'saving' : requestState}
          nextButtonText={text.nextButtonNext}
          onNext={handleComplete}
          onPrevious={onPrevious}
        />
      }
    />
  );
};

export default IndicatorCompleted;
